const UINT32_RANGE = 0x1_0000_0000;

function hashSeed(seed: string): () => number {
  let hash = 1_779_033_703 ^ seed.length;
  for (let index = 0; index < seed.length; index += 1) {
    hash = Math.imul(hash ^ seed.charCodeAt(index), 3_432_918_353);
    hash = (hash << 13) | (hash >>> 19);
  }

  return () => {
    hash = Math.imul(hash ^ (hash >>> 16), 2_246_822_507);
    hash = Math.imul(hash ^ (hash >>> 13), 3_266_489_909);
    return (hash ^= hash >>> 16) >>> 0;
  };
}

export class SeededRandom {
  private stateA: number;
  private stateB: number;
  private stateC: number;
  private stateD: number;

  public constructor(public readonly seed: string) {
    const seedHash = hashSeed(seed);
    this.stateA = seedHash();
    this.stateB = seedHash();
    this.stateC = seedHash();
    this.stateD = seedHash();
  }

  public next(): number {
    this.stateA >>>= 0;
    this.stateB >>>= 0;
    this.stateC >>>= 0;
    this.stateD >>>= 0;

    const result = (this.stateA + this.stateB + this.stateD) >>> 0;
    this.stateD = (this.stateD + 1) >>> 0;
    this.stateA = this.stateB ^ (this.stateB >>> 9);
    this.stateB = (this.stateC + (this.stateC << 3)) >>> 0;
    this.stateC = ((this.stateC << 21) | (this.stateC >>> 11)) >>> 0;
    this.stateC = (this.stateC + result) >>> 0;
    return result / UINT32_RANGE;
  }

  public int(minInclusive: number, maxInclusive: number): number {
    if (!Number.isInteger(minInclusive) || !Number.isInteger(maxInclusive)) {
      throw new TypeError("SeededRandom.int bounds must be integers");
    }
    if (maxInclusive < minInclusive) {
      throw new RangeError("SeededRandom.int maximum must not be smaller than minimum");
    }
    return minInclusive + Math.floor(this.next() * (maxInclusive - minInclusive + 1));
  }

  public range(minInclusive: number, maxExclusive: number): number {
    if (maxExclusive < minInclusive) {
      throw new RangeError("SeededRandom.range maximum must not be smaller than minimum");
    }
    return minInclusive + this.next() * (maxExclusive - minInclusive);
  }

  public chance(probability: number): boolean {
    if (probability < 0 || probability > 1) {
      throw new RangeError("Probability must be between 0 and 1");
    }
    return this.next() < probability;
  }

  public pick<T>(values: readonly T[]): T {
    if (values.length === 0) {
      throw new RangeError("Cannot pick from an empty collection");
    }
    return values[this.int(0, values.length - 1)] as T;
  }

  public shuffle<T>(values: readonly T[]): T[] {
    const shuffled = [...values];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const otherIndex = this.int(0, index);
      [shuffled[index], shuffled[otherIndex]] = [shuffled[otherIndex] as T, shuffled[index] as T];
    }
    return shuffled;
  }

  public fork(label: string): SeededRandom {
    return new SeededRandom(`${this.seed}:${label}:${Math.floor(this.next() * UINT32_RANGE)}`);
  }
}
